import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getChessCoachSettings, saveChessCoachSettings } from '../../api/integrations';
import Button from '../../components/common/Button';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { TextInput, CheckboxField } from '../../components/common/form';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { apiErrorMessage } from '../../utils/apiError';

function StatusChip({ on, t }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
        on ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
      }`}
    >
      {on ? t('settings.report.statusOn') : t('settings.report.statusOff')}
    </span>
  );
}

const DEFAULTS = {
  enabled: false,
  apiKey: '',
  baseUrl: 'https://api.openai.com/v1',
  model: 'gpt-4o-mini',
  maxTokens: 700,
  timeoutMs: 45000,
  maxHistoryMessages: 12,
  rateLimitPerMinute: 20,
  liveRatedIdeasOnly: true,
  analysisDepthFast: 10,
  analysisDepthStandard: 14,
  analysisMultipv: 3,
  configured: false,
  operational: false,
};

export default function ChessCoachSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const s = await getChessCoachSettings();
      setSettings({ ...DEFAULTS, ...s });
    } catch {
      setError(t('settings.chessCoach.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  const keyConfigured = settings?.apiKey === 'CONFIGURED' || !!settings?.configured;
  const canEnable = keyConfigured || !!newApiKey.trim();
  const canSave = !settings?.enabled || canEnable;

  const dirtyValue = settings ? { settings, newApiKey } : null;
  const persist = async () => {
    const payload = {
      ...settings,
      apiKey: newApiKey.trim() ? newApiKey.trim() : settings.apiKey,
    };
    const saved = await saveChessCoachSettings(payload);
    setSettings({ ...DEFAULTS, ...saved });
    setNewApiKey('');
    return { settings: { ...DEFAULTS, ...saved }, newApiKey: '' };
  };
  const { markSaved } = useUnsavedChanges(dirtyValue, {
    enabled: !!settings,
    onSave: async () => {
      await persist();
    },
  });

  const save = async (e) => {
    e?.preventDefault?.();
    if (!canSave) {
      setError(t('settings.chessCoach.enableWarning'));
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const next = await persist();
      markSaved(next);
      setSuccess(t('settings.chessCoach.saveSuccess'));
    } catch (err) {
      setError(apiErrorMessage(err, t('settings.chessCoach.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = useMemo(() => {
    if (!settings) return '';
    if (settings.operational) return t('settings.chessCoach.modeOperational');
    if (settings.enabled) return t('settings.chessCoach.modeEnabledIncomplete');
    if (settings.configured) return t('settings.chessCoach.modeConfigured');
    return t('settings.chessCoach.modeIncomplete');
  }, [settings, t]);

  if (loading) {
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('settings.chessCoach.title')}
        subtitle={t('settings.chessCoach.loadingSubtitle')}
      />
    );
  }

  if (!settings) {
    return (
      <div className="page-shell-form">
        <PageHeaderSection
          eyebrow={t('nav.system')}
          title={t('settings.chessCoach.title')}
          subtitle={t('settings.chessCoach.subtitle')}
        />
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="status">
          {error || t('settings.chessCoach.loadFailed')}
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell-form">
      <PageHeaderSection
        eyebrow={t('nav.system')}
        title={t('settings.chessCoach.title')}
        subtitle={t('settings.chessCoach.subtitle')}
      />

      <form onSubmit={save} className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          {t('settings.chessCoach.infoBanner')}
        </div>

        {success && (
          <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {success}
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                  {t('settings.chessCoach.stepOne')}
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                  {t('settings.chessCoach.connectionSection')}
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                  {t('settings.chessCoach.connectionSectionDesc')}
                </p>
              </div>
              <div className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                <p className="text-xs font-medium text-slate-500">{t('settings.chessCoach.statusLabel')}</p>
                <p className="text-sm font-semibold text-slate-900">{statusLabel}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 px-5 py-5 sm:px-6">
            <div
              className={`flex items-center justify-between gap-4 rounded-xl border p-4 ${
                settings.enabled
                  ? 'border-primary-200 bg-primary-50/40'
                  : 'border-slate-200 bg-slate-50/60'
              }`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900">{t('settings.chessCoach.enableTitle')}</p>
                  <StatusChip on={!!settings.enabled} t={t} />
                </div>
                <p className="mt-1 text-sm text-slate-600">{t('settings.chessCoach.enableHint')}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={settings.enabled}
                onClick={() => {
                  if (!settings.enabled && !canEnable) {
                    setError(t('settings.chessCoach.enableWarning'));
                    return;
                  }
                  update('enabled', !settings.enabled);
                }}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  settings.enabled ? 'bg-primary-600' : 'bg-slate-200'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${
                    settings.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <TextInput
                label={t('settings.chessCoach.baseUrl')}
                placeholder="https://api.openai.com/v1"
                value={settings.baseUrl || ''}
                onChange={(e) => update('baseUrl', e.target.value)}
                helper={t('settings.chessCoach.baseUrlHint')}
              />
              <TextInput
                label={t('settings.chessCoach.model')}
                placeholder="gpt-4o-mini"
                value={settings.model || ''}
                onChange={(e) => update('model', e.target.value)}
                helper={t('settings.chessCoach.modelHint')}
              />
              <div className="md:col-span-2">
                <TextInput
                  label={t('settings.chessCoach.apiKey')}
                  type="password"
                  autoComplete="new-password"
                  placeholder={
                    keyConfigured
                      ? t('settings.chessCoach.apiKeyConfigured')
                      : t('settings.chessCoach.apiKeyPlaceholder')
                  }
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  helper={t('settings.chessCoach.apiKeyHint')}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
              {t('settings.chessCoach.stepTwo')}
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">
              {t('settings.chessCoach.limitsSection')}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              {t('settings.chessCoach.limitsSectionDesc')}
            </p>
          </div>
          <div className="grid gap-4 px-5 py-5 sm:px-6 md:grid-cols-2">
            <TextInput
              label={t('settings.chessCoach.maxTokens')}
              type="number"
              value={settings.maxTokens ?? 700}
              onChange={(e) => update('maxTokens', Number(e.target.value))}
            />
            <TextInput
              label={t('settings.chessCoach.rateLimit')}
              type="number"
              value={settings.rateLimitPerMinute ?? 20}
              onChange={(e) => update('rateLimitPerMinute', Number(e.target.value))}
            />
            <TextInput
              label={t('settings.chessCoach.maxHistory')}
              type="number"
              value={settings.maxHistoryMessages ?? 12}
              onChange={(e) => update('maxHistoryMessages', Number(e.target.value))}
            />
            <TextInput
              label={t('settings.chessCoach.timeoutMs')}
              type="number"
              value={settings.timeoutMs ?? 45000}
              onChange={(e) => update('timeoutMs', Number(e.target.value))}
            />
            <CheckboxField
              className="md:col-span-2"
              layout="toggle-card"
              checked={!!settings.liveRatedIdeasOnly}
              onChange={(e) => update('liveRatedIdeasOnly', e.target.checked)}
              label={t('settings.chessCoach.liveRatedIdeasOnly')}
              description={t('settings.chessCoach.liveRatedIdeasOnlyDesc')}
            />
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-end gap-3">
          {!canSave && (
            <p className="mr-auto text-sm text-amber-800">{t('settings.chessCoach.enableWarning')}</p>
          )}
          <Button type="submit" disabled={saving || !canSave}>
            {saving ? t('forms.common.saving') : t('settings.chessCoach.saveSettings')}
          </Button>
        </div>
      </form>
    </div>
  );
}
